import { keyBy } from 'lodash-es';
import { createElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckBox, CheckBoxOutlineBlank, VFlex, VStack } from '@/package';

import PrivacyTermItem from './PrivacyTermItem';
import { TERM_ITEM_LIST } from './constant';
import type { TermItem, TermItemName } from './type';

type PrivacyAgreeProps = {
  defaultCheckedMap?: Partial<Record<TermItemName, boolean>>;
  hiddenItem?: {
    required?: boolean;
    optional?: boolean;
  };
  hiddenTitle?: boolean;
  requiredList?: TermItemName[];
  onChangeAgree(termItemList: (TermItem & { checked: boolean })[]): void;
};

const PrivacyAgree = ({
  defaultCheckedMap,
  hiddenItem,
  hiddenTitle,
  requiredList = [
    'termsOfService',
    'personalInfoCollection',
    'personalInfoTransfer',
  ],
  onChangeAgree,
}: PrivacyAgreeProps) => {
  const { t } = useTranslation();
  const [termItemMap, setTermItemMap] = useState<
    Dictionary<With<TermItem, { checked: boolean }>>
  >(
    keyBy(
      TERM_ITEM_LIST.map(v => ({
        ...v,
        checked: defaultCheckedMap?.[v.name] ?? false,
      })),
      i => i.name,
    ),
  );
  const termItemList = useMemo(() => {
    let termItemList = Object.values(termItemMap) as With<
      TermItem,
      { checked: boolean }
    >[];

    if (requiredList != null && requiredList.length > 0) {
      termItemList = termItemList.map(item => {
        if (!requiredList.includes(item.name as TermItemName))
          return { ...item };

        return { ...item, required: true };
      });
    }

    return termItemList.filter(item =>
      item.required
        ? hiddenItem?.required
          ? false
          : true
        : hiddenItem?.optional
          ? false
          : true,
    );
  }, [JSON.stringify(termItemMap), requiredList]);
  const allChecked = useMemo(
    () => termItemList.reduce((acc, i) => acc && i.checked, true),
    [termItemList],
  );

  const toogleAllCheck = () => {
    setTermItemMap(
      keyBy(
        TERM_ITEM_LIST.map(v => ({ ...v, checked: !allChecked })),
        i => i.name,
      ),
    );
  };

  useEffect(() => {
    onChangeAgree(termItemList);
  }, [JSON.stringify(termItemList.map(item => item.checked))]);

  return (
    <VStack className="w-[296px]" gap={termItemList.length > 1 ? 1 : 0.25}>
      {!hiddenTitle && (
        <div className="text-body-xl text-black">
          <strong>{t('auth.privacyAgreeTitle.dentatocrenate')}</strong>
        </div>
      )}

      <VStack className="divide-y divide-gray-200" gap={0.5}>
        {termItemList.length > 1 && (
          <VFlex className="py-1" gap={0.25} items="center">
            <div
              className="cursor-pointer text-primary-500"
              role="checkbox"
              onClick={toogleAllCheck}
            >
              <input
                id="allCheck"
                className="hidden"
                type="checkbox"
                defaultChecked={allChecked}
              />
              {createElement(!allChecked ? CheckBoxOutlineBlank : CheckBox, {
                className: 'size-4',
              })}
            </div>
            <div className="text-body-md text-gray-600">
              <strong>
                <label htmlFor="allCheck" className="cursor-pointer">
                  {t('auth.privacyAgreeAllAgreeLabel.dephlegmate')}
                </label>
              </strong>
            </div>
          </VFlex>
        )}

        <VStack className="divide-y divide-gray-200">
          {termItemList.map(item => {
            return (
              <PrivacyTermItem
                key={item.name}
                termItem={item}
                onTermAgreeChecked={checked => {
                  setTermItemMap(prev => ({
                    ...prev,
                    [item.name]: { ...item, checked },
                  }));
                }}
              />
            );
          })}
        </VStack>
      </VStack>
    </VStack>
  );
};

PrivacyAgree.displayName = 'PrivacyAgree';

export default PrivacyAgree;
